import { AlertTriangle, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { MIN_JUSTIFICATION_LENGTH } from '@obraplan/core/domain/planning/over-capacity-policy';
import type {
  BoardActivity,
  BoardDay,
  BoardService,
} from '@obraplan/core/domain/planning/weekly-board';
import { Hours, QUARTERS_PER_HOUR } from '@obraplan/core/domain/shared/hours';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import {
  asksForJustification,
  type TeamAllocationRefusal,
  type TeamAllocationSubmit,
  type TeamAllocationTeam,
} from './team-allocation';

/**
 * Alocar a equipe (Fase 30, etapa 3 — D8, RN-423, RN-424).
 *
 * Uma equipe, uma frente × serviço e os dias da semana. Em cada dia, **as horas de cada pessoa**
 * (as mesmas para todos) e **a meta da equipe** — o servidor a divide em partes iguais entre quem
 * está na equipe naquele dia. Embaixo de cada dia aparece quem vai receber a alocação: quem entra
 * na equipe na quarta não aparece na segunda.
 *
 * O serviço é obrigatório: a equipe mede uma vez por frente × serviço (RN-406), e a frente sem
 * serviço não teria o que medir.
 *
 * A recusa do servidor aparece aqui dentro, com o nome de quem a causou ("Maria Souza: …"). Se
 * for a hora extra (RN-045), o diálogo pede o motivo e reenvia o mesmo pedido com ele — que vale
 * para quem passar da jornada.
 */

export interface TeamAllocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teams: readonly TeamAllocationTeam[];
  days: readonly BoardDay[];
  activities: readonly BoardActivity[];
  services: readonly BoardService[];
  /** A equipe já escolhida, ao abrir pela linha dela na grade. */
  initialTeamId?: string;
  /** Devolve a recusa, ou nada quando gravou (quem fecha o diálogo é a página). */
  onSubmit: (input: TeamAllocationSubmit) => Promise<TeamAllocationRefusal | undefined>;
}

const pessoas = (quantas: number): string =>
  `${String(quantas)} ${quantas === 1 ? 'pessoa' : 'pessoas'}`;

const numero = (texto: string): number => Number(texto.trim().replace(',', '.'));

export function TeamAllocationDialog({
  open,
  onOpenChange,
  teams,
  days,
  activities,
  services,
  initialTeamId,
  onSubmit,
}: TeamAllocationDialogProps) {
  const [teamId, setTeamId] = useState('');
  const [activityId, setActivityId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [horas, setHoras] = useState<Record<string, string>>({});
  const [metas, setMetas] = useState<Record<string, string>>({});
  const [recusa, setRecusa] = useState<TeamAllocationRefusal | undefined>(undefined);
  /** A recusa foi a hora extra: o motivo passa a ser pedido, e o botão reenvia com ele. */
  const [pedeMotivo, setPedeMotivo] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTeamId(initialTeamId ?? teams[0]?.teamId ?? '');
    setActivityId('');
    setServiceId('');
    setHoras({});
    setMetas({});
    setRecusa(undefined);
    setPedeMotivo(false);
    setMotivo('');
    // `teams` é derivado a cada render da página; o que reabre o diálogo é `open`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTeamId]);

  /** Mexer no pedido é ler a recusa: a próxima tentativa traz a resposta nova, se houver. */
  const mudou = () => {
    setRecusa(undefined);
    setPedeMotivo(false);
  };

  const equipe = teams.find((team) => team.teamId === teamId);
  const frente = activities.find((activity) => activity.activityId === activityId);

  const porTorre = useMemo(() => {
    const grupos = new Map<string, BoardActivity[]>();
    for (const activity of activities) {
      const lista = grupos.get(activity.towerName);
      if (lista === undefined) grupos.set(activity.towerName, [activity]);
      else lista.push(activity);
    }
    // Na ordem de quem procura: a disciplina, e o pavimento pelo número ("4º" antes do "12º").
    const ordem = (a: BoardActivity, b: BoardActivity) =>
      a.disciplineName.localeCompare(b.disciplineName, 'pt-BR') ||
      a.floorName.localeCompare(b.floorName, 'pt-BR', { numeric: true });
    return [...grupos.entries()].map(([torre, frentes]) => [torre, frentes.sort(ordem)] as const);
  }, [activities]);

  // Os serviços ativos da disciplina da frente — o mesmo filtro da alocação da pessoa.
  const servicosDaFrente = useMemo(
    () =>
      frente === undefined
        ? []
        : services
            .filter((service) => service.disciplineId === frente.disciplineId && service.isActive)
            .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [services, frente],
  );
  const servico = servicosDaFrente.find((service) => service.serviceId === serviceId);
  const casas = servico?.unitDecimals ?? 2;

  const membrosDe = (date: string) => equipe?.membersByDate?.[date];

  const dias = days.flatMap((day) => {
    const bruto = (horas[day.date] ?? '').trim();
    if (bruto === '') return [];
    const quarterHours = Math.round(numero(bruto) * QUARTERS_PER_HOUR);
    if (!Number.isFinite(quarterHours) || quarterHours <= 0) return [];
    // A meta só vai com o dia que tem hora, e só quando o serviço mede.
    const metaBruta = (metas[day.date] ?? '').trim();
    const quantity =
      servico?.unitName === undefined || metaBruta === '' ? undefined : numero(metaBruta);
    const metaValida = quantity !== undefined && Number.isFinite(quantity) && quantity >= 0;
    return [{ date: day.date, quarterHours, ...(metaValida ? { quantity } : {}) }];
  });

  /** O dia com horas em que a equipe não tem ninguém — o servidor recusaria (RN-423). */
  const diaSemNinguem = dias.find((dia) => membrosDe(dia.date)?.length === 0);

  const horasDaEquipe = dias.reduce(
    (soma, dia) => soma + dia.quarterHours * (membrosDe(dia.date)?.length ?? 0),
    0,
  );
  const sabeQuem = equipe?.membersByDate !== undefined;

  const motivoCurto = motivo.trim().length < MIN_JUSTIFICATION_LENGTH;
  const valido =
    teamId !== '' &&
    activityId !== '' &&
    serviceId !== '' &&
    dias.length > 0 &&
    diaSemNinguem === undefined &&
    !(pedeMotivo && motivoCurto);

  const enviar = async () => {
    setEnviando(true);
    try {
      const resposta = await onSubmit({
        teamId,
        activityId,
        serviceId,
        days: dias,
        ...(pedeMotivo ? { justification: motivo.trim() } : {}),
        ...(frente?.hasBlocker === true ? { acknowledgeRestriction: true } : {}),
      });
      if (resposta === undefined) return;
      setRecusa(resposta);
      if (asksForJustification(resposta.code)) setPedeMotivo(true);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Alocar equipe</DialogTitle>
          <DialogDescription>
            Cada pessoa da equipe no dia recebe as mesmas horas; a meta é da equipe e é dividida em
            partes iguais. Quem fizer diferente muda a dele depois, na grade.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-w-0 flex-col gap-4">
          <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Equipe" htmlFor="equipe-alocada" required>
              <select
                id="equipe-alocada"
                value={teamId}
                onChange={(event) => {
                  setTeamId(event.target.value);
                  mudou();
                }}
                className="h-11 w-full min-w-0 rounded-(--radius-field) border border-(--color-border-strong) bg-(--color-surface) px-3"
              >
                {teams.length === 0 && <option value="">Nenhuma equipe ativa</option>}
                {teams.map((team) => (
                  <option key={team.teamId} value={team.teamId}>
                    {team.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Frente" htmlFor="frente-da-equipe" required>
              <select
                id="frente-da-equipe"
                value={activityId}
                onChange={(event) => {
                  setActivityId(event.target.value);
                  // Trocar de frente troca a disciplina: o serviço e a meta (na unidade dele) saem.
                  setServiceId('');
                  setMetas({});
                  mudou();
                }}
                className="h-11 w-full min-w-0 rounded-(--radius-field) border border-(--color-border-strong) bg-(--color-surface) px-3"
              >
                <option value="">Escolha a frente…</option>
                {porTorre.map(([torre, frentes]) => (
                  <optgroup key={torre} label={torre}>
                    {frentes.map((activity) => (
                      <option key={activity.activityId} value={activity.activityId}>
                        {activity.disciplineName} — {activity.floorName}
                        {activity.hasBlocker === true ? ' · impedida' : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </Field>
          </div>

          {frente !== undefined && (
            <Field
              label="Serviço"
              htmlFor="servico-da-equipe"
              required
              {...(servicosDaFrente.length === 0
                ? {
                    hint: `Nenhum serviço ativo em ${frente.disciplineName}. Cadastre em Configurações → Serviços.`,
                  }
                : {})}
            >
              <select
                id="servico-da-equipe"
                value={serviceId}
                disabled={servicosDaFrente.length === 0}
                onChange={(event) => {
                  setServiceId(event.target.value);
                  setMetas({});
                  mudou();
                }}
                className="h-11 w-full min-w-0 rounded-(--radius-field) border border-(--color-border-strong) bg-(--color-surface) px-3"
              >
                <option value="">Escolha o serviço…</option>
                {servicosDaFrente.map((service) => (
                  <option key={service.serviceId} value={service.serviceId}>
                    {service.name}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {frente?.hasBlocker === true && (
            <p className="flex items-start gap-2 text-sm text-(--color-state-warn)">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              Esta frente tem impedimento aberto. A equipe é alocada assim mesmo, e o aviso fica
              registrado.
            </p>
          )}

          <fieldset className="flex min-w-0 flex-col gap-2 rounded-(--radius-card) border border-(--color-border-subtle) bg-(--color-surface-muted) p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <legend className="text-sm font-medium">
                Dias <span className="text-(--color-state-danger)">*</span>
                <span className="ml-1 font-normal text-(--color-content-muted)">
                  horas de cada pessoa
                  {servico?.unitName === undefined
                    ? ''
                    : ` · meta da equipe em ${servico.unitName}`}
                </span>
              </legend>
              {dias.length > 0 && (
                <span className="tabular text-sm text-(--color-content-muted)">
                  {dias.length} {dias.length === 1 ? 'dia' : 'dias'}
                  {sabeQuem && horasDaEquipe > 0 && (
                    <>
                      {' · '}
                      <strong className="text-(--color-content)">
                        {Hours.ofQuarters(horasDaEquipe).format()}
                      </strong>{' '}
                      da equipe
                    </>
                  )}
                </span>
              )}
            </div>

            <div className="grid min-w-0 grid-cols-1 gap-2">
              {days.map((day) => {
                const rotulo = `${day.weekdayLabel} ${day.shortLabel}`;
                const temHora = (horas[day.date] ?? '').trim() !== '';
                const membros = membrosDe(day.date);
                return (
                  <div
                    key={day.date}
                    className={cn(
                      'grid min-w-0 grid-cols-2 items-center gap-2 rounded-md border p-2 sm:grid-cols-[6rem_minmax(0,1fr)_minmax(0,1fr)]',
                      day.isWorkday
                        ? 'border-(--color-border-subtle) bg-(--color-surface)'
                        : 'border-transparent',
                    )}
                  >
                    <span className="col-span-2 flex items-baseline gap-1.5 sm:col-span-1">
                      <span
                        className={cn(
                          'text-sm font-semibold uppercase tracking-wide',
                          !day.isWorkday && 'text-(--color-content-muted)',
                        )}
                      >
                        {day.weekdayLabel}
                      </span>
                      <span className="text-xs text-(--color-content-muted)">{day.shortLabel}</span>
                    </span>

                    <Input
                      type="number"
                      inputMode="decimal"
                      step={0.25}
                      min={0}
                      placeholder="horas"
                      aria-label={`Horas de cada pessoa em ${rotulo}`}
                      className="tabular h-11 min-w-0 text-center md:h-11"
                      value={horas[day.date] ?? ''}
                      onChange={(event) => {
                        setHoras((atual) => ({ ...atual, [day.date]: event.target.value }));
                        mudou();
                      }}
                    />

                    {servico?.unitName === undefined ? (
                      <span className="hidden sm:block" />
                    ) : (
                      <Input
                        type="number"
                        inputMode="decimal"
                        min={0}
                        step={10 ** -casas}
                        placeholder="meta"
                        disabled={!temHora}
                        aria-label={`Meta da equipe em ${rotulo}, em ${servico.unitName}`}
                        className="tabular h-11 min-w-0 text-center md:h-11"
                        value={metas[day.date] ?? ''}
                        onChange={(event) => {
                          setMetas((atual) => ({ ...atual, [day.date]: event.target.value }));
                          mudou();
                        }}
                      />
                    )}

                    {membros !== undefined && (
                      <div className="col-span-2 min-w-0 sm:col-span-3">
                        {membros.length === 0 ? (
                          <p
                            className={cn(
                              'text-xs',
                              temHora
                                ? 'font-medium text-(--color-state-danger)'
                                : 'text-(--color-content-muted)',
                            )}
                          >
                            Ninguém da equipe pode ser alocado neste dia.
                          </p>
                        ) : (
                          <div
                            className={cn(
                              'flex min-w-0 items-start gap-1.5 text-xs',
                              temHora ? 'text-(--color-content)' : 'text-(--color-content-muted)',
                            )}
                          >
                            <Users className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
                            <ul
                              aria-label={`Quem recebe em ${rotulo}`}
                              className="flex min-w-0 flex-wrap gap-x-2 gap-y-0.5"
                            >
                              {membros.map((membro) => (
                                <li key={membro.resourceId} className="min-w-0 truncate">
                                  {membro.name}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </fieldset>

          {diaSemNinguem !== undefined && (
            <p role="status" className="text-sm text-(--color-state-danger)">
              Tire as horas do dia sem ninguém na equipe, ou aloque as pessoas uma a uma.
            </p>
          )}

          {recusa !== undefined && (
            <p role="alert" className="text-sm text-(--color-state-danger)">
              {recusa.message}
            </p>
          )}

          {pedeMotivo && (
            <Field
              label="Motivo da hora extra"
              htmlFor="motivo-da-equipe"
              required
              hint={`Vale para quem passar da jornada. Fica registrado na alocação e na auditoria. Mínimo de ${String(MIN_JUSTIFICATION_LENGTH)} caracteres.`}
            >
              <Textarea
                id="motivo-da-equipe"
                rows={3}
                value={motivo}
                onChange={(event) => setMotivo(event.target.value)}
                placeholder="Ex.: concretagem da laje no sábado, autorizada pelo gestor."
              />
            </Field>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant={pedeMotivo ? 'danger' : 'primary'}
            disabled={!valido || enviando}
            onClick={() => void enviar()}
          >
            {pedeMotivo ? 'Alocar com hora extra' : 'Alocar equipe'}
            {equipe?.membersByDate !== undefined &&
              dias.length > 0 &&
              ` (${pessoas(
                new Set(dias.flatMap((dia) => (membrosDe(dia.date) ?? []).map((m) => m.resourceId)))
                  .size,
              )})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
